import { memo, useEffect, useRef, useState } from 'react';

import { styled } from '@mui/system';
import simpleheat from 'simpleheat';

import {
  TAiSimulationPartialDto,
  TPrototypePartialDto,
} from '@app-velocity/av-api-v2';

import { Loader } from '@app-velocity/animations';

import { PageAction, PreviewHighlightOverlay } from '@app-velocity/common';

import { useWorker } from '../ViewPreview/hooks/useWorker';
import { WorkerPage, WorkerPartial, WorkerPartialResult } from '../../types';
import { DebugLayer } from './components/DebugLayer';

export type ViewPreviewProps = {
  partialsResults: TPrototypePartialDto[];
  partials: TAiSimulationPartialDto[];
  page: {
    width: number;
    height: number;
    density: number;
    image: string;
    actions?: PageAction[];
  };
  clicks?: { x: number; y: number }[];
  size: 'full' | 'large' | 'normal';
  persona: 'accessible' | 'default' | string;
  noFilters?: boolean;
  highlight?: boolean;
};

const StyledViewPreview = styled('div', {
  shouldForwardProp: (prop) => prop !== 'highlight',
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: highlight ? theme.spacing(4) : 0,
  borderRadius: highlight ? theme.spacing(2) : 0,
  backgroundColor: highlight
    ? 'var(--background-background-secondary-color)'
    : 'transparent',
}));

const StyledCanvas = styled('canvas')<{ ready: boolean }>(
  ({ theme, ready }) => ({
    display: 'block',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    boxShadow: `0px ${theme.spacing(2)} ${theme.spacing(
      3
    )} 0px rgba(0, 0, 0, 0.1)`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    opacity: ready ? 1 : 0,
    transition: 'opacity .3s',
  })
);

const CanvasContainer = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  objectFit: 'contain',
}));

const CanvasWrapper = styled('div')({
  position: 'relative',
  display: 'inline-block',
  maxWidth: '100%',
  maxHeight: '100%',
});

const LoaderContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  // background: 'rgba(0, 0, 0, 0.5)',
  width: theme.spacing(20),
  height: theme.spacing(20),
  left: '50%',
  top: '50%',
  borderRadius: theme.spacing(2),
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
}));

const getSize = (size: 'full' | 'large' | 'normal') => {
  switch (size) {
    case 'full':
      return document.body.offsetHeight - 100;
    case 'large':
      return 500;
    default:
      return 250;
  }
};

export const ViewPreviewContent = memo(
  ({
    page,
    partials,
    partialsResults,
    clicks,
    size,
    persona,
    noFilters = false,
    highlight: isHighlighted = false,
  }: ViewPreviewProps) => {
    const ref = useRef<HTMLCanvasElement | null>(null);

    const [dataId] = useState(
      () => new Date().getTime() + Math.round(Math.random() * 1000)
    );

    const { ready, done, postMessage } = useWorker({
      canvasRef: ref,
    });

    const canvasToImageBitmap = async (
      canvas: HTMLCanvasElement
    ): Promise<ImageBitmap> => {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png')
      );
      if (!blob) throw new Error('Failed to convert canvas to Blob');

      return await createImageBitmap(blob);
    };

    const transformData = (data: { x: number; y: number }[]) => {
      const map: { [coord: string]: number } = {};

      data.forEach((point) => {
        const x = Math.round(point.x || 0);
        const y = Math.round(point.y || 0);

        map[`${x}|${y}`] = (map[`${x}|${y}`] || 0) + 1;
      });

      return Object.keys(map).map((key) => {
        const splited = key.split('|');

        return [Number(splited[0]), Number(splited[1]), map[key]];
      });
    };

    const generateHeatmap = async (
      clicks: { x: number; y: number }[],
      page: { image: string; width: number; height: number; density: number }
    ): Promise<ImageBitmap | null> => {
      const targetHeight = getSize(size);
      const multiplier = Math.max(1, Math.floor(height / targetHeight) - 2);
      if (!clicks.length) return null;
      const canvas = document.createElement('canvas');
      canvas.width = page.width!;
      canvas.height = page.height!;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const transformedData = transformData(clicks || []);

      const heat = simpleheat(canvas);
      heat.data([...transformedData]);
      heat.max(2);
      heat.radius(
        multiplier * 25 * (page.density || 1),
        multiplier * 15 * (page.density || 1)
      );
      heat.draw();

      return await canvasToImageBitmap(canvas);
    };

    const handleRender = async (
      clicks: { x: number; y: number }[],
      page: { width: number; height: number; image: string; density: number },
      partials: TAiSimulationPartialDto[],
      partialsResults: TPrototypePartialDto[],
      persona: 'accessible' | 'default' | string
    ) => {
      const heatMap = await generateHeatmap(clicks || [], page);

      postMessage(
        {
          type: 'render',
          page: {
            image: page.image,
            width: page.width,
            height: page.height,
          } as WorkerPage,
          partials: partials as WorkerPartial[],
          partialsResults: partialsResults.map((p) => ({
            id: p.id,
            image: p.image,
          })) as WorkerPartialResult[],
          size,
          heatMap,
          persona,
          bodyHeight: document.body.offsetHeight,
          noFilters,
        },
        heatMap ? [heatMap] : []
      );
    };

    useEffect(() => {
      if (!page || !partialsResults || !partials) return;
      handleRender(clicks || [], page, partials, partialsResults, persona);
    }, [page, partials, partialsResults, clicks, size, persona]);

    if (!page) return null;

    const { width, height } = page;

    if (!width || !height) return;

    const targetHeight = getSize(size);
    const factor = height / targetHeight;
    const targetWidth = Math.round(width / factor);

    return (
      <StyledViewPreview highlight={isHighlighted}>
        {(!ready || !done) && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}
        <DebugLayer
          page={{
            width: page.width,
            height: page.height,
            actions: page.actions || [],
          }}
          partials={partials}
          viewPartials={partialsResults}
        />
        <CanvasContainer>
          <CanvasWrapper>
            <StyledCanvas
              ready={ready && done}
              data-id={dataId}
              ref={ref}
              width={targetWidth}
              height={targetHeight}
            />
            <PreviewHighlightOverlay />
          </CanvasWrapper>
        </CanvasContainer>
      </StyledViewPreview>
    );
  }
);
